/**
 * Card Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import { BaseAiComponentInput } from '../../registry'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ComponentAlignment,
  LimitInput,
  LimitSettings,
  ResponsiveValue,
  SpacingInputValue,
  SpacingUnit,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseAspectRatioFormat,
  parseColorFormat,
  parseGapFormat,
  parseLimitFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatFromVariableText,
  formatLimitFormat,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
  formatComponentAlignmentFormat,
  formatColorFormat,
  formatResponsiveValue,
  formatCornerRadiusFormat,
  formatAspectRatioFormat,
  formatOrientationFormat,
  formatGapFormat,
} from '../../format-utils'
import cardTemplate from './card.template.json'

export interface ICardComponentSettings {
  // Data settings
  'card-data-source': string
  'card-data-thumbnail': string
  'card-data-header': string
  'card-data-subheader': string
  'card-data-media': string
  'card-data-title': string
  'card-data-subtitle': string
  'card-data-supporting-text': string
  'card-data-metadata-1': any
  'card-data-trailing-header-menu': any
  'card-data-footer': any
  'card-data-action-help': BuilderActions
  'card-data-leading-action': any
  'card-data-trailing-action': any
  'card-data-trailing-menu': any
  'card-data-comp-title': VariableTextValue
  'card-data-sort-by': string
  'card-data-sort-type': string
  'card-data-filter': any
  'card-data-group-by': any
  'card-data-num-items': LimitSettings

  // Design settings
  'card-design-color-mode': any
  'card-design-color-tone-order': any
  'card-design-color-start-palette': any
  'card-design-bg-color': string
  'card-design-scheme': any
  'card-design-bg-opacity': string
  'card-design-header-color-variant': string
  'card-design-subheader-color-variant': string
  'card-design-title-color-variant': string
  'card-design-subtitle-color-variant': string
  'card-design-supporting-text-color-variant': string
  'card-design-metadata-color-variant': string
  'card-design-leading-button-scheme': ColorAdaptiveValue<string>
  'card-design-trailing-button-scheme': ColorAdaptiveValue<string>
  'card-design-comp-title-color': any
  'card-design-group-header-color': any
  'card-design-search-bg-color': string
  'card-design-filter-chip-color': any
  'card-design-filter-bg-color': string
  'card-design-add-button-color': any
  'card-design-group-text-color': any
  'card-design-text-1-color': any
  'card-design-text-1-color-solid': ColorAdaptiveValue<ColorVariant>
  'card-design-text-1-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-text-1-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-text-1-color-gradient-direction': ColorAdaptiveValue<ColorVariant>
  'card-design-text-2-color': any
  'card-design-text-2-color-solid': ColorAdaptiveValue<ColorVariant>
  'card-design-text-2-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-text-2-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-text-2-color-gradient-direction': ColorAdaptiveValue<ColorVariant>
  'card-design-text-3-color': any
  'card-design-text-3-color-solid': ColorAdaptiveValue<ColorVariant>
  'card-design-text-3-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-text-3-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-text-3-color-gradient-direction': ColorAdaptiveValue<ColorVariant>
  'card-design-text-4-color': any
  'card-design-text-4-color-solid': ColorAdaptiveValue<ColorVariant>
  'card-design-text-4-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-text-4-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-text-4-color-gradient-direction': ColorAdaptiveValue<ColorVariant>
  'card-design-text-5-color': any
  'card-design-text-5-color-solid': ColorAdaptiveValue<ColorVariant>
  'card-design-text-5-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-text-5-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-text-5-color-gradient-direction': ColorAdaptiveValue<ColorVariant>
  'card-design-metadata-color': any
  'card-design-metadata-color-solid': ColorAdaptiveValue<ColorVariant>
  'card-design-metadata-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-metadata-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-metadata-color-gradient-direction': ColorAdaptiveValue<ColorVariant>
  'card-design-footer-area-color': any
  'card-design-footer-area-color-solid': ColorAdaptiveValue<ColorVariant>
  'card-design-footer-area-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-footer-area-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'card-design-footer-area-color-gradient-direction': ColorAdaptiveValue<ColorVariant>
  'card-design-group-header-color-solid': any
  'card-design-group-header-color-start-gradient': any
  'card-design-group-header-color-end-gradient': any
  'card-design-group-header-color-gradient-direction': any
  'card-design-comp-title-color-solid': any
  'card-design-comp-title-color-start-gradient': any
  'card-design-comp-title-color-end-gradient': any
  'card-design-comp-title-color-gradient-direction': any
  'card-design-variant': string
  'card-design-no-border': string[]
  'card-design-no-hover-bg': any
  'card-design-no-padding': SpacingInputValue
  'card-design-leading-button-variant': string
  'card-design-trailing-button-variant': string
  'card-design-width': ResponsiveValue<string[] | number[]>
  'card-design-leading-button-full-width': ResponsiveValue<string[] | number[]>
  'card-design-trailing-button-full-width': ResponsiveValue<string[] | number[]>
  'card-design-search-box': ResponsiveValue<any>
  'card-design-shape': string
  'card-design-thumb-shape': string
  'card-design-media-shape': string
  'card-design-leading-btn-shape': string
  'card-design-trailing-btn-shape': string
  'card-design-search-corner': any
  'card-design-filter-corner-radius': string
  'card-design-add-button-corner-radius': string
  'card-design-icon-variant': string
  'card-design-icon-background-color-variant': string
  'card-design-icon-color-variant': string
  'card-design-icon-color-custom': any
  'card-design-icon-background-color-column': string
  'card-design-icon-color-column': any
  'card-design-media-icon-variant': ResponsiveValue<string>
  'card-design-media-icon-background-color-variant': string
  'card-design-media-icon-color-variant': string
  'card-design-media-icon-color-custom': ResponsiveValue<any>
  'card-design-media-icon-color-filled': ResponsiveValue<any>
  'card-design-media-icon-color-column': ResponsiveValue<any>
  'card-design-media-icon-background-color-column': string
  'card-design-media-icon-size': ResponsiveValue<string>
  'card-design-media-icon-weight': ResponsiveValue<any>
  'card-design-media-icon-grade': ResponsiveValue<any>
  'card-design-orientation': ResponsiveValue<string>
  'card-design-num-cols': ResponsiveValue<any>
  'card-design-num-rows': any
  'card-design-pagination': ResponsiveValue<any>
  'card-design-column-row': ResponsiveValue<any>
  'card-design-same-height': ResponsiveValue<string[] | number[]>
  'card-design-margin': ResponsiveValue<SpacingInputValue>
  'card-design-media-margin': ResponsiveValue<SpacingInputValue>
  'card-design-headline-margin': ResponsiveValue<SpacingInputValue>
  'card-design-group-header-margin': ResponsiveValue<SpacingInputValue>
  'card-design-footer-margin': ResponsiveValue<SpacingInputValue>
  'card-design-item-gap': ResponsiveValue<{ value: number; unit: SpacingUnit }>
  'card-design-item-padding': SpacingInputValue
  'card-design-padding': ResponsiveValue<SpacingInputValue>
  'card-design-meta-margin': ResponsiveValue<SpacingInputValue>
  'card-design-search-margin': ResponsiveValue<SpacingInputValue>
  'card-design-filter-margin': ResponsiveValue<SpacingInputValue>
  'card-design-media-aspect-ratio': ResponsiveValue<any>
  'card-design-divider-between-title': any
  'card-design-divider': any
  'card-design-comp-h-align': ResponsiveValue<ComponentAlignment>
  'card-design-comp-title-align': ResponsiveValue<any>
  'card-design-group-header-align': ResponsiveValue<any>
  'card-design-header-title-align': ResponsiveValue<any>
  'card-design-header-subtitle-align': ResponsiveValue<any>
  'card-design-title-align': ResponsiveValue<any>
  'card-design-subtitle-align': ResponsiveValue<any>
  'card-design-supporting-text-align': ResponsiveValue<any>
  'card-design-meta-align': ResponsiveValue<any>
  'card-design-media-align': ResponsiveValue<any>
  'card-design-search-align': ResponsiveValue<any>
  'card-design-pagination-align': ResponsiveValue<any>
  'card-design-typography': {
    'card-design-action-help-typography': ResponsiveValue<TypographyValue>
    'card-design-group-header-typography': ResponsiveValue<TypographyValue>
    'card-design-header-typography': ResponsiveValue<TypographyValue>
    'card-design-headline-typography': ResponsiveValue<TypographyValue>
    'card-design-metadata-typography': ResponsiveValue<TypographyValue>
    'card-design-subheader-typography': ResponsiveValue<TypographyValue>
    'card-design-subtitle-typography': ResponsiveValue<TypographyValue>
    'card-design-supporting-text-typography': ResponsiveValue<TypographyValue>
    'card-design-title-typography': ResponsiveValue<TypographyValue>
  }
  'card-design-header-lines': ResponsiveValue<any>
  'card-design-subheader-lines': ResponsiveValue<any>
  'card-design-title-lines': ResponsiveValue<any>
  'card-design-subtitle-lines': ResponsiveValue<any>
  'card-design-supporting-text-lines': ResponsiveValue<any>
  'card-design-metadata-lines': any

  // Actions
  'card-data-leading-action-icon': BuilderActions
  'card-data-leading-action-icon-position': BuilderActions
  'card-data-leading-action-label': BuilderActions
  'card-data-trailing-action-icon': BuilderActions
  'card-data-trailing-action-icon-position': BuilderActions
  'card-data-trailing-action-label': BuilderActions
  'card-design-action-align': BuilderActions
  'card-design-action-help-typography': BuilderActions
  'card-action-item-click': BuilderActions
  'card-action-leading-click': BuilderActions
  'card-action-trailing-click': BuilderActions
  'card-action-add-button-click': BuilderActions

  // Behavior settings
  'card-behavior-user-filter': boolean
  'card-behavior-user-search': boolean
  'card-behavior-search-results-info': boolean
  'card-behavior-add-button-show': boolean
  'card-behavior-add-button-position': boolean
  'card-behavior-add-button-visibility': boolean
}

export interface ICardComponent extends IBuilderComponent {
  componentId: 'card'
  groupId: 'list'
  settings: ICardComponentSettings
}

/**
 * Card AI Input
 */
export interface AiCardInput extends BaseAiComponentInput {
  dataSource: string // Required: Table ID
  cardTitle?: string[] // Format: ["text:Card List"] or ["text:Items in ", "cr:category"]

  // Data columns
  titleColumn?: string // Column ID for card title
  subtitleColumn?: string // Column ID for card subtitle
  supportingTextColumn?: string // Column ID for supporting text
  headerColumn?: string // Column ID for header
  subheaderColumn?: string // Column ID for subheader
  mediaColumn?: string // Column ID for media (images)
  thumbnailColumn?: string // Column ID for thumbnail
  footerColumn?: string // Column ID for footer

  // Sort & Filter
  sortBy?: string // Column ID to sort by
  sortType?: 'asc' | 'desc' // Sort direction
  limit?: LimitInput // Number of items to display

  // Layout
  orientation?: 'vertical' | 'horizontal' // Card orientation
  columns?: number | string // Number of columns (e.g., 1, 2, 3 or "1 ta:2 de:3")
  rows?: number // Number of rows per page
  variant?: string // Card variant

  // Styling
  width?: string // Format: "w:100%" or "w:100% de:w:60%"
  alignment?: string // Format: "center" or "left"
  titleAlignment?: string // Title alignment
  margin?: string // Format: "m:16px"
  padding?: string // Padding for card
  itemPadding?: string // Padding for each card item
  itemGap?: string // Gap between cards
  cornerRadius?: string // Corner radius: "none", "small", "medium", "large" or "extra-small", "extra-large", "full"
  mediaCornerRadius?: string // Media corner radius
  mediaRatio?: string // Media aspect ratio: "1:1", "16:9", "4:3"

  // Colors
  titleColor?: string // Title color
  backgroundColor?: string // Card background color

  // Typography
  typography?: {
    title?: string // Title typography
    subtitle?: string // Subtitle typography
    supportingText?: string // Supporting text typography
    header?: string // Header typography
    subheader?: string // Subheader typography
  }

  // Behavior
  showSearch?: boolean // Show search bar
  showFilter?: boolean // Show filter
  showAddButton?: boolean // Show add button
  showPagination?: boolean // Show pagination
}

const CARD_DEFAULT_TEMPLATE = cardTemplate as unknown as ICardComponentSettings

export class CardComp {
  getTemplate(): ICardComponentSettings {
    return CARD_DEFAULT_TEMPLATE
  }

  /**
   * format card component settings to AI card input
   */
  format(settings: ICardComponentSettings): Omit<AiCardInput, 'type'> {
    const result: Omit<AiCardInput, 'type'> = {
      dataSource: settings['card-data-source'] || '',
    }

    // Data - Source (required)
    if (settings['card-data-source']) {
      result.dataSource = settings['card-data-source']
    }

    // Data - Columns
    if (settings['card-data-title']) {
      result.titleColumn = settings['card-data-title']
    }

    if (settings['card-data-subtitle']) {
      result.subtitleColumn = settings['card-data-subtitle']
    }

    if (settings['card-data-supporting-text']) {
      result.supportingTextColumn = settings['card-data-supporting-text']
    }

    if (settings['card-data-header']) {
      result.headerColumn = settings['card-data-header']
    }

    if (settings['card-data-subheader']) {
      result.subheaderColumn = settings['card-data-subheader']
    }

    if (settings['card-data-media']) {
      result.mediaColumn = settings['card-data-media']
    }

    if (settings['card-data-thumbnail']) {
      result.thumbnailColumn = settings['card-data-thumbnail']
    }

    if (settings['card-data-footer']) {
      result.footerColumn = settings['card-data-footer']
    }

    // Data - Component Title
    if (settings['card-data-comp-title']) {
      result.cardTitle = formatFromVariableText(settings['card-data-comp-title'])
    }

    // Data - Sort
    if (settings['card-data-sort-by']) {
      result.sortBy = settings['card-data-sort-by']
    }

    if (settings['card-data-sort-type']) {
      result.sortType = settings['card-data-sort-type'] as 'asc' | 'desc'
    }

    // Data - Limit
    if (settings['card-data-num-items']) {
      result.limit = formatLimitFormat(settings['card-data-num-items'])
    }

    // Layout - Variant
    if (settings['card-design-variant']) {
      result.variant = settings['card-design-variant']
    }

    // Layout - Orientation
    if (settings['card-design-orientation']) {
      const orientation = formatOrientationFormat(settings['card-design-orientation'])
      if (orientation === 'vertical' || orientation === 'horizontal') {
        result.orientation = orientation
      }
    }

    // Layout - Columns
    if (settings['card-design-num-cols']) {
      const allSame = Object.values(settings['card-design-num-cols']).every((v) => v === Object.values(settings['card-design-num-cols'])[0])
      if (allSame) {
        result.columns = Object.values(settings['card-design-num-cols'])[0]
      } else {
        result.columns = formatResponsiveValue(settings['card-design-num-cols'], (val) => String(val))
      }
    }

    // Layout - Rows
    if (settings['card-design-num-rows']) {
      result.rows = settings['card-design-num-rows'] as number
    }

    // Design - Width
    if (settings['card-design-width']) {
      result.width = formatWidthFormat(settings['card-design-width'])
    }

    // Design - Alignment
    if (settings['card-design-comp-h-align']) {
      result.alignment = formatComponentAlignmentFormat(settings['card-design-comp-h-align'])?.replace(/^h:/, '') || undefined
    }

    // Design - Title Alignment
    if (settings['card-design-comp-title-align']) {
      const allSame = Object.values(settings['card-design-comp-title-align']).every((v) => v === Object.values(settings['card-design-comp-title-align'])[0])
      if (allSame) {
        result.titleAlignment = String(Object.values(settings['card-design-comp-title-align'])[0])
      } else {
        result.titleAlignment = formatComponentAlignmentFormat(settings['card-design-comp-title-align'] as any)?.replace(/^h:/, '') || undefined
      }
    }

    // Design - Margin
    if (settings['card-design-margin']) {
      result.margin = formatSpacingFormat(settings['card-design-margin'])
    }

    // Design - Padding
    if (settings['card-design-padding']) {
      result.padding = formatSpacingFormat(settings['card-design-padding'])
    }

    // Design - Item Padding
    if (settings['card-design-item-padding']) {
      result.itemPadding = formatSpacingFormat({ phone: settings['card-design-item-padding'] } as any)
    }

    // Design - Item Gap
    if (settings['card-design-item-gap']) {
      result.itemGap = formatGapFormat(settings['card-design-item-gap'])
    }

    // Design - Corner Radius
    if (settings['card-design-shape']) {
      result.cornerRadius = formatCornerRadiusFormat(settings['card-design-shape'])
    }

    // Design - Media Corner Radius
    if (settings['card-design-media-shape']) {
      result.mediaCornerRadius = formatCornerRadiusFormat(settings['card-design-media-shape'])
    }

    // Design - Media Ratio
    if (settings['card-design-media-aspect-ratio']) {
      result.mediaRatio = formatAspectRatioFormat(settings['card-design-media-aspect-ratio'])
    }

    // Design - Title Color
    if (settings['card-design-comp-title-color']) {
      result.titleColor = formatColorFormat({
        colorType: settings['card-design-comp-title-color'] as any,
        colorSolid: settings['card-design-comp-title-color-solid'],
        colorStartGradient: settings['card-design-comp-title-color-start-gradient'],
        colorEndGradient: settings['card-design-comp-title-color-end-gradient'],
        colorGradientDirection: settings['card-design-comp-title-color-gradient-direction'],
      })
    }

    // Design - Background Color
    if (settings['card-design-bg-color']) {
      result.backgroundColor = settings['card-design-bg-color']
    }

    // Design - Typography
    if (settings['card-design-typography']) {
      result.typography = {}
      if (settings['card-design-typography']['card-design-title-typography']) {
        result.typography.title = formatTypographyFormat(settings['card-design-typography']['card-design-title-typography'])
      }
      if (settings['card-design-typography']['card-design-subtitle-typography']) {
        result.typography.subtitle = formatTypographyFormat(settings['card-design-typography']['card-design-subtitle-typography'])
      }
      if (settings['card-design-typography']['card-design-supporting-text-typography']) {
        result.typography.supportingText = formatTypographyFormat(settings['card-design-typography']['card-design-supporting-text-typography'])
      }
      if (settings['card-design-typography']['card-design-header-typography']) {
        result.typography.header = formatTypographyFormat(settings['card-design-typography']['card-design-header-typography'])
      }
      if (settings['card-design-typography']['card-design-subheader-typography']) {
        result.typography.subheader = formatTypographyFormat(settings['card-design-typography']['card-design-subheader-typography'])
      }
    }

    // Behavior - Search
    if (settings['card-behavior-user-search'] !== undefined) {
      result.showSearch = settings['card-behavior-user-search']
    }

    // Behavior - Filter
    if (settings['card-behavior-user-filter'] !== undefined) {
      result.showFilter = settings['card-behavior-user-filter']
    }

    // Behavior - Add Button
    if (settings['card-behavior-add-button-show'] !== undefined) {
      result.showAddButton = settings['card-behavior-add-button-show']
    }

    // Behavior - Pagination
    if (settings['card-design-pagination']) {
      const allSame = Object.values(settings['card-design-pagination']).every((v) => v === Object.values(settings['card-design-pagination'])[0])
      if (allSame) {
        result.showPagination = Object.values(settings['card-design-pagination'])[0] as boolean
      } else {
        // For responsive values, use phone value as default
        result.showPagination = Boolean(settings['card-design-pagination'].phone)
      }
    }

    return result
  }

  /**
   * parse AI card input to card component settings
   */
  parse(input: AiCardInput): ICardComponentSettings {
    const result = deepMerge(CARD_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['card-data-source'] = input.dataSource
    }

    // Data - Columns
    if (input.titleColumn) {
      result['card-data-title'] = input.titleColumn
    }

    if (input.subtitleColumn) {
      result['card-data-subtitle'] = input.subtitleColumn
    }

    if (input.supportingTextColumn) {
      result['card-data-supporting-text'] = input.supportingTextColumn
    }

    if (input.headerColumn) {
      result['card-data-header'] = input.headerColumn
    }

    if (input.subheaderColumn) {
      result['card-data-subheader'] = input.subheaderColumn
    }

    if (input.mediaColumn) {
      result['card-data-media'] = input.mediaColumn
    }

    if (input.thumbnailColumn) {
      result['card-data-thumbnail'] = input.thumbnailColumn
    }

    if (input.footerColumn) {
      result['card-data-footer'] = input.footerColumn
    }

    // Data - Component Title
    if (input.cardTitle) {
      result['card-data-comp-title'] = parseToVariableText(input.cardTitle)
    }

    // Data - Sort
    if (input.sortBy) {
      result['card-data-sort-by'] = input.sortBy
    }

    if (input.sortType) {
      result['card-data-sort-type'] = input.sortType
    }

    // Data - Limit
    if (input.limit !== undefined) {
      const parsedLimit = parseLimitFormat(input.limit)
      if (parsedLimit) {
        result['card-data-num-items'] = parsedLimit
      }
    }

    // Design - Variant
    if (input.variant) {
      result['card-design-variant'] = input.variant
    }

    // Design - Width
    if (input.width) {
      result['card-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['card-design-comp-h-align'] = alignmentParsed.hAlign
      }
    }

    if (input.titleAlignment) {
      const titleAlignParsed = parseAlignmentFormat(input.titleAlignment)
      if (titleAlignParsed.hAlign) {
        result['card-design-comp-title-align'] = titleAlignParsed.hAlign
      }
    }

    // Design - Margin
    if (input.margin) {
      result['card-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Padding
    if (input.padding) {
      result['card-design-padding'] = parseSpacingFormat(input.padding)
    }

    // Design - Item Padding (non-responsive)
    if (input.itemPadding) {
      const paddingParsed = parseSpacingFormat(input.itemPadding)
      result['card-design-item-padding'] = paddingParsed.phone
    }

    // Design - Item Gap (simple responsive value, not full spacing object)
    if (input.itemGap) {
      result['card-design-item-gap'] = parseGapFormat(input.itemGap)
    }

    // Design - Shape
    if (input.cornerRadius) {
      result['card-design-shape'] = input.cornerRadius
    }

    // Design - Media Shape
    if (input.mediaCornerRadius) {
      result['card-design-media-shape'] = input.mediaCornerRadius
    }

    // Design - Media Ratio
    if (input.mediaRatio) {
      result['card-design-media-aspect-ratio'] = parseAspectRatioFormat(input.mediaRatio)
    }

    // Design - Orientation
    if (input.orientation) {
      result['card-design-orientation'] = parseResponsiveFormat(input.orientation, (val) => val)
    }

    // Design - Columns
    if (input.columns !== undefined) {
      if (typeof input.columns === 'string') {
        result['card-design-num-cols'] = parseResponsiveFormat(input.columns, (val) => parseInt(val))
      } else {
        result['card-design-num-cols'] = parseResponsiveFormat(input.columns.toString(), (val) => parseInt(val))
      }
    }

    // Design - Rows (items per page)
    if (input.rows !== undefined) {
      result['card-design-num-rows'] = input.rows
    }

    // Design - Title Color
    if (input.titleColor) {
      const colorParsed = parseColorFormat(input.titleColor)
      result['card-design-comp-title-color'] = colorParsed.colorType
      result['card-design-comp-title-color-solid'] = colorParsed.colorSolid
      result['card-design-comp-title-color-start-gradient'] = colorParsed.colorStartGradient
      result['card-design-comp-title-color-end-gradient'] = colorParsed.colorEndGradient
      result['card-design-comp-title-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Background Color
    if (input.backgroundColor) {
      const bgColorParsed = parseColorFormat(input.backgroundColor)
      result['card-design-bg-color'] = bgColorParsed.colorSolid as any
    }

    // Design - Typography
    if (input.typography) {
      if (!result['card-design-typography']) {
        result['card-design-typography'] = {} as any
      }

      if (input.typography.title) {
        result['card-design-typography']['card-design-title-typography'] = parseTypographyFormat(input.typography.title)
      }

      if (input.typography.subtitle) {
        result['card-design-typography']['card-design-subtitle-typography'] = parseTypographyFormat(
          input.typography.subtitle
        )
      }

      if (input.typography.supportingText) {
        result['card-design-typography']['card-design-supporting-text-typography'] = parseTypographyFormat(
          input.typography.supportingText
        )
      }

      if (input.typography.header) {
        result['card-design-typography']['card-design-header-typography'] = parseTypographyFormat(
          input.typography.header
        )
      }

      if (input.typography.subheader) {
        result['card-design-typography']['card-design-subheader-typography'] = parseTypographyFormat(
          input.typography.subheader
        )
      }
    }

    // Behavior - Search
    if (input.showSearch !== undefined) {
      result['card-behavior-user-search'] = input.showSearch
    }

    // Behavior - Filter
    if (input.showFilter !== undefined) {
      result['card-behavior-user-filter'] = input.showFilter
    }

    // Behavior - Add Button
    if (input.showAddButton !== undefined) {
      result['card-behavior-add-button-show'] = input.showAddButton
    }

    // Behavior - Pagination
    if (input.showPagination !== undefined) {
      result['card-design-pagination'] = parseResponsiveFormat(input.showPagination.toString(), (val) => val === 'true')
    }

    return result
  }
}
